import {
  Box,
  Collapse,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Checkbox from '@components/Checkbox';
import { ConfirmationDialog } from '@components/ConfirmationDialog';
import Dropdown from '@components/Dropdown';
import { FormikTextField } from '@components/FormikFields';
import IconButton from '@components/IconButton';

import type { OrgFeatureModel } from 'generated/types';

import { DESCRIPTION_CHAR_LIMIT } from './plan-helpers';
import type { PatientPlanValue } from './types';

const messages = defineMessages({
  planName: {
    id: 'patientPlans.planName',
    defaultMessage: 'Plan name',
  },
  unnamedPlan: {
    id: 'patientPlans.unnamedPlan',
    defaultMessage: 'Plan {number}',
  },
  summary: {
    id: 'patientPlans.cardSummary',
    defaultMessage: '{selected} of {total} features · {seats} seats',
  },
  edit: {
    id: 'patientPlans.edit',
    defaultMessage: 'Edit',
  },
  collapse: {
    id: 'patientPlans.collapse',
    defaultMessage: 'Collapse',
  },
  remove: {
    id: 'patientPlans.remove',
    defaultMessage: 'Remove',
  },
  removeTitle: {
    id: 'patientPlans.removeConfirm',
    defaultMessage: 'Remove {name}?',
  },
  removeBody: {
    id: 'patientPlans.removeConfirm.body',
    defaultMessage:
      'The seats assigned to it go back to the unassigned pool. This cannot be undone.',
  },
  moveUp: {
    id: 'patientPlans.moveUp',
    defaultMessage: 'Move plan up',
  },
  moveDown: {
    id: 'patientPlans.moveDown',
    defaultMessage: 'Move plan down',
  },
  description: {
    id: 'patientPlans.description',
    defaultMessage: 'Description',
  },
  descriptionPlaceholder: {
    id: 'patientPlans.description.placeholder',
    defaultMessage: 'Who this plan is for',
  },
  seats: {
    id: 'patientPlans.seats',
    defaultMessage: 'Seats for this plan',
  },
  includedFeatures: {
    id: 'patientPlans.includedFeatures',
    defaultMessage: 'Included patient features',
  },
  fromOrgFeature: {
    id: 'patientPlans.fromOrgFeature',
    defaultMessage: 'from {name}',
  },
  selectAll: {
    id: 'patientPlans.selectAll',
    defaultMessage: 'Select all',
  },
  deselectAll: {
    id: 'patientPlans.deselectAll',
    defaultMessage: 'Deselect all',
  },
  copyFrom: {
    id: 'patientPlans.copyFrom',
    defaultMessage: 'Copy from…',
  },
  copyTitle: {
    id: 'patientPlans.copyConfirm',
    defaultMessage: 'Copy features from {name}?',
  },
  copyBody: {
    id: 'patientPlans.copyConfirm.body',
    defaultMessage:
      'This replaces the features selected on this plan. The two plans stay independent afterwards — later edits to {name} will not follow.',
  },
  noFeatures: {
    id: 'patientPlans.noFeatures',
    defaultMessage: 'No patient features are available for this organization.',
  },
});

/**
 * `Link` has no disabled styling of its own, so the button attribute alone
 * would leave a dead control looking interactive.
 */
const DISABLED_LINK_SX = { '&:disabled': { color: 'text.disabled' } };

const SEATS_FIELD_WIDTH = 200;
/** Matches the height of the single-line inputs it sits beside. */
const DESCRIPTION_FIELD_HEIGHT = 47;
/** Gap between feature tiles, in px, and the `gap={1.5}` spacing below. */
const FEATURE_GRID_GAP = 12;

interface PatientPlanCardProps {
  /** Field path of this plan in the enclosing Formik form, e.g. `plans[0]`. */
  namePrefix: string;
  /** Position in the list: drives the badge number and the testids */
  index: number;
  plan: PatientPlanValue;
  /** Every plan on the step, so "Copy from…" can label them by their badge */
  plans: PatientPlanValue[];
  /** Patient features this organization has enabled */
  eligibleFeatures: OrgFeatureModel[];
  expanded: boolean;
  onToggleExpanded: () => void;
  onRemove: () => void;
  /** Omitted at the ends of the list, where the move is not possible */
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onCopyFrom: (source: PatientPlanValue) => void;
  onSetFeatureIds: (featureIds: string[]) => void;
  disabled?: boolean;
}

export default function PatientPlanCard({
  namePrefix,
  index,
  plan,
  plans,
  eligibleFeatures,
  expanded,
  onToggleExpanded,
  onRemove,
  onMoveUp,
  onMoveDown,
  onCopyFrom,
  onSetFeatureIds,
  disabled,
}: PatientPlanCardProps) {
  const { formatMessage } = useIntl();
  // The plan the admin picked from "Copy from…", held until they confirm.
  const [copySourceId, setCopySourceId] = React.useState('');

  /** A plan shows its own badge number wherever it is referred to. */
  function planLabel(source: PatientPlanValue, sourceIndex: number) {
    return (
      source.name.trim() ||
      formatMessage(messages.unnamedPlan, { number: sourceIndex + 1 })
    );
  }

  const allFeaturesSelected =
    eligibleFeatures.length > 0 &&
    eligibleFeatures.every(({ feature_id }) =>
      plan.feature_ids.includes(feature_id)
    );

  const otherPlans = plans
    .map((source, sourceIndex) => ({ source, sourceIndex }))
    .filter(({ sourceIndex }) => sourceIndex !== index);
  const copySource = otherPlans.find(
    ({ source }) => source.local_id === copySourceId
  );

  function handleToggleFeature(featureId: string, checked: boolean) {
    onSetFeatureIds(
      checked
        ? [...plan.feature_ids, featureId]
        : plan.feature_ids.filter((id) => id !== featureId)
    );
  }

  function handleConfirmCopy() {
    if (copySource) {
      // A one-time snapshot: the plans have no relationship afterwards.
      onCopyFrom(copySource.source);
    }
  }

  return (
    <Paper
      data-testid={`patient-plan-card-${index}`}
      // The theme's MuiPaper root sets `border: none` after the `outlined`
      // variant, cancelling it. Not redundant — do not remove.
      sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}
      variant="outlined"
    >
      <Stack p={2} spacing={1.5}>
        <Stack
          alignItems="center"
          direction="row"
          flexWrap="wrap"
          justifyContent="space-between"
          spacing={1.5}
        >
          <Stack alignItems="center" direction="row" spacing={1.5}>
            <Box
              alignItems="center"
              borderRadius="50%"
              display="flex"
              flexShrink={0}
              height={26}
              justifyContent="center"
              // Tinted from primary rather than a fixed pale blue, which the
              // dark palette does not define.
              sx={(theme) => ({
                bgcolor: alpha(theme.palette.primary.main, 0.14),
                color: 'primary.main',
              })}
              width={26}
            >
              <Typography component="span" variant="subtitle2">
                {index + 1}
              </Typography>
            </Box>
            {/* The number badge names the plan, so the input carries no label
                of its own — it reads as the card's title. */}
            <FormikTextField
              disabled={disabled}
              name={`${namePrefix}.name`}
              placeholder={formatMessage(messages.planName)}
            />
          </Stack>

          <Stack alignItems="center" direction="row" spacing={1}>
            {/* The icon is aria-hidden and the tooltip is not announced, so
                the buttons carry their own labels. */}
            <IconButton
              disabled={disabled || !onMoveUp}
              iconButtonProps={{
                'aria-label': formatMessage(messages.moveUp),
              }}
              iconType="fas"
              name="arrow-up"
              onClick={() => onMoveUp?.()}
              tooltip={formatMessage(messages.moveUp)}
            />
            <IconButton
              disabled={disabled || !onMoveDown}
              iconButtonProps={{
                'aria-label': formatMessage(messages.moveDown),
              }}
              iconType="fas"
              name="arrow-down"
              onClick={() => onMoveDown?.()}
              tooltip={formatMessage(messages.moveDown)}
            />
            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
            <Link
              component="button"
              data-testid={`patient-plan-toggle-${index}`}
              disabled={disabled}
              onClick={onToggleExpanded}
              sx={DISABLED_LINK_SX}
              type="button"
              underline="hover"
              variant="button"
            >
              {formatMessage(expanded ? messages.collapse : messages.edit)}
            </Link>
            <ConfirmationDialog
              content={formatMessage(messages.removeBody)}
              onConfirm={async () => onRemove()}
              title={formatMessage(messages.removeTitle, {
                name: planLabel(plan, index),
              })}
              variant="error"
            >
              {({ handleOpen }) => (
                <Link
                  component="button"
                  data-testid={`patient-plan-remove-${index}`}
                  disabled={disabled}
                  onClick={handleOpen}
                  // The theme's MuiLink hard-sets `color: info.main`, which
                  // beats the `color` prop; sx is what lands.
                  sx={{ color: 'error.main', ...DISABLED_LINK_SX }}
                  type="button"
                  underline="hover"
                  variant="button"
                >
                  {formatMessage(messages.remove)}
                </Link>
              )}
            </ConfirmationDialog>
          </Stack>
        </Stack>

        <Typography
          color="text.secondary"
          data-testid={`patient-plan-summary-${index}`}
          variant="body2"
        >
          {formatMessage(messages.summary, {
            selected: plan.feature_ids.length,
            total: eligibleFeatures.length,
            seats: Number(plan.seats) || 0,
          })}
        </Typography>

        <Collapse in={expanded} unmountOnExit>
          <Stack spacing={2}>
            <Divider />

            <Stack
              alignItems="flex-start"
              direction="row"
              flexWrap="wrap"
              spacing={2}
            >
              <Box flex={1} minWidth={260}>
                {/* A single-line box matching the seats field beside it, per
                    the design — not a drag-to-resize text area. */}
                <FormikTextField
                  charLimit={DESCRIPTION_CHAR_LIMIT}
                  disabled={disabled}
                  expandable={false}
                  fullWidth
                  height={DESCRIPTION_FIELD_HEIGHT}
                  label={formatMessage(messages.description)}
                  name={`${namePrefix}.description`}
                  placeholder={formatMessage(messages.descriptionPlaceholder)}
                  type="text-area"
                />
              </Box>
              <Box width={SEATS_FIELD_WIDTH}>
                <FormikTextField
                  disabled={disabled}
                  fullWidth
                  label={formatMessage(messages.seats)}
                  name={`${namePrefix}.seats`}
                  placeholder="0"
                  type="number"
                />
              </Box>
            </Stack>

            <Stack spacing={1}>
              <Stack
                alignItems="center"
                direction="row"
                flexWrap="wrap"
                justifyContent="space-between"
                spacing={1}
              >
                <Typography
                  color="text.secondary"
                  component="h4"
                  variant="overline"
                >
                  {formatMessage(messages.includedFeatures)}
                </Typography>

                <Stack alignItems="center" direction="row" spacing={1.5}>
                  {/* Hidden rather than disabled when this is the only plan:
                      there is nothing to copy from. */}
                  {otherPlans.length > 0 && (
                    <Box width={180}>
                      <Dropdown
                        data-testid={`patient-plan-copy-from-${index}`}
                        disabled={disabled}
                        height="36px"
                        onChange={setCopySourceId}
                        placeholder={formatMessage(messages.copyFrom)}
                        // Held at "" so the placeholder returns after a copy and
                        // the same source can be picked again.
                        value=""
                      >
                        {otherPlans.map(({ source, sourceIndex }) => (
                          <option key={source.local_id} value={source.local_id}>
                            {planLabel(source, sourceIndex)}
                          </option>
                        ))}
                      </Dropdown>
                    </Box>
                  )}
                  {otherPlans.length > 0 && eligibleFeatures.length > 0 && (
                    <Divider flexItem orientation="vertical" />
                  )}
                  {eligibleFeatures.length > 0 && (
                    <Link
                      component="button"
                      data-testid={`patient-plan-select-all-${index}`}
                      disabled={disabled}
                      onClick={() =>
                        onSetFeatureIds(
                          allFeaturesSelected
                            ? []
                            : eligibleFeatures.map(
                                ({ feature_id }) => feature_id
                              )
                        )
                      }
                      sx={DISABLED_LINK_SX}
                      type="button"
                      underline="hover"
                      // Sentence case per the design: `variant="button"` would
                      // upper-case it like the EDIT/REMOVE actions.
                      variant="body2"
                    >
                      {formatMessage(
                        allFeaturesSelected
                          ? messages.deselectAll
                          : messages.selectAll
                      )}
                    </Link>
                  )}
                </Stack>
              </Stack>

              {eligibleFeatures.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  {formatMessage(messages.noFeatures)}
                </Typography>
              ) : (
                <Box
                  display="grid"
                  gap={`${FEATURE_GRID_GAP}px`}
                  // A minimum track of a third of the row caps the grid at
                  // three across; auto-fit collapses unused tracks so short
                  // lists still span the width.
                  gridTemplateColumns={{
                    xs: '1fr',
                    sm: `repeat(auto-fit, minmax(max(220px, (100% - ${
                      2 * FEATURE_GRID_GAP
                    }px) / 3), 1fr))`,
                  }}
                >
                  {eligibleFeatures.map((feature) => (
                    <Paper
                      data-testid={`patient-plan-feature-${index}-${feature.name}`}
                      key={feature.feature_id}
                      // As on the card's own Paper, the theme cancels the
                      // `outlined` variant, so the border is set here.
                      // Selection reads as a fill, so it stays neutral.
                      sx={(theme) => ({
                        bgcolor: plan.feature_ids.includes(feature.feature_id)
                          ? alpha(theme.palette.primary.main, 0.08)
                          : 'transparent',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        px: 1.5,
                        py: 1,
                      })}
                      variant="outlined"
                    >
                      <Checkbox
                        checked={plan.feature_ids.includes(feature.feature_id)}
                        disabled={disabled}
                        label={
                          <Stack spacing={0}>
                            <Typography color="text.primary" variant="body2">
                              {feature.patient_display_name}
                            </Typography>
                            <Typography
                              color="text.secondary"
                              variant="caption"
                            >
                              {formatMessage(messages.fromOrgFeature, {
                                name: feature.display_name,
                              })}
                            </Typography>
                          </Stack>
                        }
                        onChange={(event) =>
                          handleToggleFeature(
                            feature.feature_id,
                            event.target.checked
                          )
                        }
                      />
                    </Paper>
                  ))}
                </Box>
              )}
            </Stack>
          </Stack>
        </Collapse>
      </Stack>

      {/* Driven by the dropdown rather than a trigger of its own, so `children`
          renders nothing and `open` is controlled here. */}
      <ConfirmationDialog
        content={formatMessage(messages.copyBody, {
          name: copySource
            ? planLabel(copySource.source, copySource.sourceIndex)
            : '',
        })}
        onClose={() => setCopySourceId('')}
        onConfirm={async () => handleConfirmCopy()}
        open={Boolean(copySource)}
        title={formatMessage(messages.copyTitle, {
          name: copySource
            ? planLabel(copySource.source, copySource.sourceIndex)
            : '',
        })}
        variant="warning"
      >
        {() => null}
      </ConfirmationDialog>
    </Paper>
  );
}
